// Placement (finishing-position) helpers shared by the record/edit UIs and
// every results display. Ties are first-class: any number of players can
// share a rank (e.g. two players tied for 2nd).

/**
 * Standard competition ranking ("1224"). Given a finishing order with a tie
 * flag per position — `tiedWithPrevious[i]` is true when the player at
 * position `i` finished level with the player at position `i - 1` — return the
 * rank to store for each player. Tied players share a rank and the next
 * distinct placement skips the consumed numbers. The flag at index 0 is
 * ignored (the leader can't tie with someone above them).
 *
 *   order:  A   B   C   D
 *   tied:   –   ✓   –   –   →  ranks 1, 1, 3, 4
 */
export const competitionRanks = (tiedWithPrevious: ReadonlyArray<boolean>): number[] => {
  const ranks: number[] = []
  for (let i = 0; i < tiedWithPrevious.length; i++) {
    const tiesUp = i > 0 && tiedWithPrevious[i] === true
    ranks.push(tiesUp ? ranks[i - 1]! : i + 1)
  }
  return ranks
}

/** The set of ranks shared by 2+ participants in a play (i.e. actual ties). */
export const tiedRanks = (ranks: ReadonlyArray<number>): Set<number> => {
  const counts = new Map<number, number>()
  for (const r of ranks) counts.set(r, (counts.get(r) ?? 0) + 1)
  const shared = new Set<number>()
  for (const [rank, count] of counts) if (count > 1) shared.add(rank)
  return shared
}

/** Ordinal suffix for a placement: "st" / "nd" / "rd" / "th" (handles 11–13). */
export const ordinalSuffix = (n: number): string => {
  const mod100 = Math.abs(n) % 100
  if (mod100 >= 11 && mod100 <= 13) return 'th'
  switch (Math.abs(n) % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

/** Human placement label, e.g. 1 → "1st", 22 → "22nd". */
export const formatOrdinal = (n: number): string => `${n}${ordinalSuffix(n)}`
